"use client";

import { useRef, type MutableRefObject } from "react";
import NextImage from "next/image";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { cn } from "@/lib/utils";
import type { PageTradingCompetitionSchema } from "@/sanity/pages/PageTradingCompetitionTemplate";
import BreakLines from "./BreakLines";
import SelectionHighlight, { type SelectionGeometry } from "./SelectionHighlight";
import { at, StableText, useCountUp, useReveal } from "./motion";

type Prize = NonNullable<PageTradingCompetitionSchema["bonusPrizes"]>[number];

/** "trip." selection: Figma 68×37 at 34px (mobile), 114×54 at 48px (desktop), measured against the glyphs. */
const TITLE_SELECTION = {
  mobile: {
    font: 34,
    box: [-5.54, -5.37, 68, 37],
    word: 72.19,
    handles: [[-0.75, 0, 1.5, 33], [-2, 33, 4, 4], [66, 0, 4, 4], [67.25, 4, 1.5, 33]],
  },
  desktop: {
    font: 48,
    box: [-13.89, -2.87, 114, 54],
    word: 105.55,
    handles: [[0, 0, 2, 54], [-2, 48, 6, 6], [110, 0, 6, 6], [112, 0, 2, 54]],
  },
} satisfies Record<string, SelectionGeometry>;

/** Figma's dividers take no height: a 1px line centred on its slot. */
function Divider({ className }: { className: string }) {
  return (
    <i aria-hidden="true" className="relative block h-0 w-full">
      <i className={cn("absolute inset-x-0 top-[-0.5px] h-px", className)} />
    </i>
  );
}

/** The large card: unveils bottom-up, the statue settles from a zoom and drifts on scroll, the
 *  amount counts up and the "+ Trip" tag pops in last. */
function FeaturedCard({ prize, image }: { prize: Prize; image?: PageTradingCompetitionSchema["bonusImage"] }) {
  const [ref, reveal] = useReveal<HTMLElement>({ threshold: 0.25 });
  const amount = useCountUp(prize.amount, reveal === "live", { delay: 0.7, duration: 1.6 });
  const reduceMotion = useReducedMotion();
  const scrollRef = useRef<HTMLElement | null>(null);
  const { scrollYProgress } = useScroll({ target: scrollRef, offset: ["start end", "end start"] });
  const statueY = useTransform(scrollYProgress, [0, 1], ["-2rem", "2rem"]);

  return (
    <article
      ref={(el) => {
        (ref as MutableRefObject<HTMLElement | null>).current = el;
        scrollRef.current = el;
      }}
      data-reveal={reveal}
      style={at(0, 1.2)}
      className="tc-rv-unveil relative isolate flex w-full shrink-0 flex-col items-start overflow-hidden rounded-[0.5rem] bg-[#003828] p-[3rem] lg:h-[72rem] lg:w-[73rem] lg:p-[4rem]">
      {/* Figma's CROP image fill: its imageTransform as a CSS box (mobile, then desktop) */}
      {image && (
        <motion.div
          className="tc-rv-zoom absolute inset-0 -z-10 origin-bottom"
          style={{ ...at(0, 1.8), ["--zoom" as string]: "1.12", ...(reduceMotion ? {} : { y: statueY }) }}>
          <NextImage
            src={image.src}
            width={image.width}
            height={image.height}
            alt=""
            sizes="(max-width: 992px) 200vw, 50vw"
            quality={90}
            className="absolute top-[5.514%] left-[-47.299%] h-[88.793%] w-[194.597%] max-w-none lg:top-[3.636%] lg:left-[-24.641%] lg:h-[89.062%] lg:w-[149.358%]"
          />
        </motion.div>
      )}
      <i
        aria-hidden="true"
        className="absolute inset-0 -z-10 bg-[linear-gradient(180.47deg,rgba(4,31,24,0)_34.465%,#041f18_75.957%)] lg:bg-[linear-gradient(180.36deg,rgba(4,31,24,0)_34.465%,#041f18_75.957%)]"
      />
      {/* Figma's "reading gradient" is drawn at the desktop size (584×576) on both layouts */}
      <i
        aria-hidden="true"
        className="absolute top-0 left-0 -z-10 h-[72rem] w-[73rem] bg-[linear-gradient(180deg,rgba(0,20,15,0)_35%,rgba(0,20,15,0.85)_75%,rgba(0,20,15,0.95)_100%)]"
      />

      <p style={at(0.45)} className="tc-rv-rise mb-0 font-geist text-[1.75rem] leading-[2.5rem] font-medium whitespace-nowrap text-[#a3deb2]">{prize.label}</p>
      <i aria-hidden="true" className="h-[31rem] shrink-0 lg:h-auto lg:min-h-[6rem] lg:flex-1" />

      <div style={at(0.55)} className="tc-rv-rise flex w-full flex-col items-start gap-[2.5rem]">
        <div className="flex w-full flex-col items-start gap-[1rem]">
          <h3 className="mb-0 font-geist text-[3.25rem] leading-[4.5rem] font-medium tracking-[-0.065rem] whitespace-nowrap text-white">
            {prize.title}
          </h3>
          <p className="mb-0 font-geist text-[2rem] leading-[3rem] font-normal text-white/[0.72] lg:w-[60.5rem]">
            {prize.description}
          </p>
        </div>
        <div className="w-full lg:w-[63rem]">
          <Divider className="bg-white/[0.18]" />
        </div>
        <div className="flex h-[8.125rem] items-center gap-[2rem]">
          <p className="mb-0 font-blacker text-[8rem] leading-[12rem] font-normal tracking-[-0.16rem] whitespace-nowrap text-[#d6eee7] italic">
            <StableText text={prize.amount} shown={amount} />
          </p>
          {prize.tag && (
            <div style={at(1.5)} className="tc-rv-pop flex h-[3.5rem] items-center rounded-[0.25rem] bg-white/10 px-[1.25rem]">
              <p className="mb-0 font-geist text-[1.75rem] leading-[normal] font-medium whitespace-nowrap text-white">
                {prize.tag}
              </p>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

/** A small card: rises in its own turn (they stagger on desktop, where all four arrive together)
 *  and its amount counts up. Figma's per-card spacing quirks are kept. */
function SmallCard({ prize, index }: { prize: Prize; index: number }) {
  const [ref, reveal] = useReveal<HTMLElement>({ threshold: 0.3 });
  const amount = useCountUp(prize.amount, reveal === "live", { delay: 0.35 + index * 0.12, duration: 1.4 });
  const compact = index === 0;
  return (
    <article
      ref={ref}
      data-reveal={reveal}
      style={at(index * 0.12, 1)}
      className="tc-rv-rise flex w-full flex-col items-start gap-[2.5rem] overflow-hidden rounded-[0.5rem] border border-solid border-black/10 bg-white p-[2.5rem] lg:h-[35rem] lg:w-[36.5rem] lg:justify-between lg:gap-0 lg:p-[3rem]">
      <p className="mb-0 font-geist text-[1.75rem] leading-[2.5rem] font-medium whitespace-nowrap text-[rgba(0,27,20,0.6)]">
        {prize.label}
      </p>
      <div
        className={cn(
          "flex w-full flex-col items-start gap-[1.75rem]",
          compact && "h-[15.375rem] justify-center [&>*]:shrink-0 lg:h-auto lg:justify-start",
        )}>
        <div className={cn("flex w-full flex-col items-start", compact ? "gap-[0.75rem]" : index === 1 ? "gap-[1.25rem]" : "gap-[1rem]")}>
          <h3 className="mb-0 font-geist text-[2.5rem] leading-[3.5rem] font-medium tracking-[-0.05rem] text-[#001b14]">
            {prize.title}
          </h3>
          <p className="mb-0 font-geist text-[1.75rem] leading-[2.5rem] font-normal text-[rgba(0,27,20,0.7)]">
            {/* the desktop cards break their copy by hand; mobile wraps freely */}
            <BreakLines text={prize.description} on="desktop" />
          </p>
        </div>
        <Divider className="bg-black/[0.07]" />
        <div className={cn("flex items-center", compact ? "h-[5.5rem] lg:h-[4.5rem]" : "h-[6rem]")}>
          <p
            className={cn(
              "mb-0 font-blacker text-[5rem] font-normal tracking-[-0.1rem] whitespace-nowrap text-[#003828] italic lg:text-[5.5rem] lg:tracking-[-0.11rem]",
              compact ? "leading-[1.1]" : "leading-[6rem]",
            )}>
            <StableText text={prize.amount} shown={amount} />
          </p>
        </div>
      </div>
    </article>
  );
}

export default function TradingCompetitionBonus({ data }: { data: PageTradingCompetitionSchema }) {
  const [headerRef, headerReveal] = useReveal();
  const [featured, ...rest] = data.bonusPrizes ?? [];
  if (!featured) return null;

  return (
    <section
      id="bonus"
      className="flex scroll-mt-[7.5rem] flex-col items-center gap-[3.5rem] bg-white px-[2rem] pt-[7rem] pb-[8rem] lg:gap-[5rem] lg:px-0 lg:py-[12.5rem]">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div ref={headerRef} data-reveal={headerReveal} className="flex w-full flex-col items-center gap-[1.75rem] lg:w-auto lg:gap-[2rem]">
        {data.bonusEyebrow && (
          <p style={at(0)} className="tc-rv-rise mb-0 font-blacker text-[2.25rem] leading-[0.9444] font-normal whitespace-nowrap text-[rgba(0,27,20,0.66)] italic lg:text-[2.5rem]">
            {data.bonusEyebrow}
          </p>
        )}

        <h2 style={at(0.12, 1)} className="tc-rv-rise mb-0 text-center font-geist text-[4.25rem] leading-[1.04] font-medium tracking-[-0.1913rem] whitespace-pre text-[#001b14] lg:text-[6rem] lg:leading-[normal] lg:tracking-[-0.3rem]">
          <BreakLines text={data.bonusTitleBefore} on="mobile" />
          {/* Figma's own spacing before the highlight: two spaces on mobile, three on desktop */}
          <span className="lg:hidden">{"  "}</span>
          <span className="hidden lg:inline">{"   "}</span>
          <span className="relative font-blacker leading-[0] font-normal text-[#228e71] italic lg:tracking-[-0.18rem] lg:text-[#003828]">
            <SelectionHighlight
              {...TITLE_SELECTION}
              fill="rgba(34,142,113,0.12)"
              mobileFill="rgba(34,142,113,0.1)"
              handle="#228e71"
              hatch="/trading-competition/highlight-hatch-bonus.png"
              delay={0.85}
            />
            <span className="relative font-blacker">{data.bonusTitleHighlight}</span>
          </span>
        </h2>

        {data.bonusDescription && (
          <p style={at(0.3)} className="tc-rv-rise mb-0 w-[42.25rem] max-w-full text-center font-geist text-[2rem] leading-[1.5] font-normal text-[rgba(0,27,20,0.66)] lg:w-[77.25rem] lg:text-[2.25rem]">
            {data.bonusDescription}
          </p>
        )}
      </div>

      {/* ── Bento: the large dark card, then a 2×2 grid ─────────────────── */}
      <div className="flex w-full flex-col items-start gap-[1.5rem] lg:w-[150rem] lg:flex-row lg:gap-[2rem]">
        <FeaturedCard prize={featured} image={data.bonusImage} />

        <div className="flex w-full flex-col gap-[1.5rem] lg:w-[75rem] lg:flex-row lg:flex-wrap lg:content-start lg:gap-[2rem]">
          {rest.map((prize, i) => (
            <SmallCard key={prize._key ?? i} prize={prize} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
